import { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Spin, InputNumber } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  CheckOutlined,
  LoadingOutlined,
  EuroCircleOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { TablesEmptyState } from "../TablesEmptyState";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import {
  useActivePaymentMethods,
  usePaymentMethods,
} from "../../../../../hooks/usePaymentMethods";
import { useSplitAndPayBill } from "../../../../../hooks/useBills";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { getBills } from "../../../../../api/bills";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useDiscountClients } from "../../../../../hooks/useDiscounts";
import { useViewport } from "../../../../../hooks/useViewport";
import { R } from "../../orderingTheme";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

interface SplitAndPayElement {
  billElementId: number;
  quantity: number;
}

interface SplitAndPayModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  billId: number;
  sourceTableName: string;
  elements: SplitAndPayElement[];
  totalAmount: number;
  canPaymentCash: boolean;
  canPaymentCard: boolean;
  canCloseBill1?: boolean;
  customerId?: number | null;
}

export const SplitAndPayModal = ({
  open,
  onClose,
  onSuccess,
  billId,
  sourceTableName,
  elements,
  totalAmount,
  canPaymentCash,
  canPaymentCard,
  canCloseBill1 = false,
  customerId = null,
}: SplitAndPayModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: rawMethods = [], isLoading } = useActivePaymentMethods();
  const { data: allMethods = [] } = usePaymentMethods();
  // id=3 (Смесено) се показва само ако имаме конфигурирани и id=1, и id=2.
  const methods = useMemo(() => {
    const hasCash = rawMethods.some((m) => m.id === 1);
    const hasCard = rawMethods.some((m) => m.id === 2);
    const withoutMixed = rawMethods.filter((m) => m.id !== 3);
    if (!hasCash || !hasCard) return withoutMixed;
    const mixed =
      rawMethods.find((m) => m.id === 3) ?? allMethods.find((m) => m.id === 3);
    if (!mixed) return withoutMixed;
    const cardIdx = withoutMixed.findIndex((m) => m.id === 2);
    const insertAt = cardIdx >= 0 ? cardIdx + 1 : withoutMixed.length;
    return [
      ...withoutMixed.slice(0, insertAt),
      mixed,
      ...withoutMixed.slice(insertAt),
    ];
  }, [rawMethods, allMethods]);
  const splitAndPayMutation = useSplitAndPayBill();
  const message = useOrderingMessage();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const queryClient = useQueryClient();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  const [mixedCashAmount, setMixedCashAmount] = useState(0);
  const [mixedCardAmount, setMixedCardAmount] = useState(0);
  const [cashClickCount, setCashClickCount] = useState(0);

  const cashOnlyMode = canCloseBill1 && cashClickCount >= 3;

  // Плащане по сметка (id=4) е ВИНАГИ видимо, но е активно САМО при избран
  // клиент с флаг can_pay_on_account (org DB) — иначе opacity + хинт.
  const { data: discountData } = useDiscountClients(open);
  const hasCustomer = customerId != null;
  const canOnAccount =
    hasCustomer &&
    (discountData?.items.find((c) => c.id === customerId)
      ?.can_pay_on_account ??
      false);

  // Ако клиентът отпадне/няма право докато id=4 е избран — върни към cash
  useEffect(() => {
    if (selectedPaymentMethodId === 4 && !canOnAccount) {
      setSelectedPaymentMethodId(1);
    }
  }, [canOnAccount, selectedPaymentMethodId]);

  const handleSubmitRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (open && methods.length > 0 && !selectedPaymentMethodId) {
      const cash = methods.find((m) => m.id === 1);
      if (cash) setSelectedPaymentMethodId(1);
    }
  }, [open, methods, selectedPaymentMethodId]);

  useEffect(() => {
    if (!open) {
      setSelectedPaymentMethodId(null);
      setMixedCashAmount(0);
      setMixedCardAmount(0);
      setCashClickCount(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !isDesktop || isLoading) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      )
        return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (!submitting && selectedPaymentMethodId) handleSubmitRef.current();
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (!submitting) onClose();
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const avail = methods.filter((m) => {
          if (m.id === 1) return canPaymentCash;
          if (m.id === 2) return canPaymentCard;
          if (m.id === 3) return canPaymentCash && canPaymentCard;
          if (m.id === 4) return canOnAccount;
          return true;
        });
        if (avail.length === 0) return;
        const idx = avail.findIndex((m) => m.id === selectedPaymentMethodId);
        const next =
          e.key === "ArrowDown"
            ? (idx + 1) % avail.length
            : idx <= 0
              ? avail.length - 1
              : idx - 1;
        setSelectedPaymentMethodId(avail[next].id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    isDesktop,
    isLoading,
    submitting,
    selectedPaymentMethodId,
    methods,
    canPaymentCash,
    canPaymentCard,
    canOnAccount,
    onClose,
  ]);

  handleSubmitRef.current = async () => {
    if (!selectedPaymentMethodId) {
      message.warning(t("closeTable.selectPaymentWarning"));
      return;
    }
    if (selectedPaymentMethodId === 3) {
      const sum = mixedCashAmount + mixedCardAmount;
      if (Math.abs(sum - totalAmount) > 0.01) {
        message.warning(
          t("closeTable.sumMismatch", {
            total: totalAmount.toFixed(2),
            entered: sum.toFixed(2),
          }),
        );
        return;
      }
    }
    setSubmitting(true);
    try {
      // Сметката може да е мръднала след избора (друг сервитьор, сторно) —
      // при самото натискане теглим свежите сметки (fetchQuery пълни и кеша
      // ["bills"]) и преизчисляваме сумата на избраните редове от тях.
      const freshBills = await queryClient.fetchQuery({
        queryKey: ["bills"],
        queryFn: getBills,
        staleTime: 0,
      });
      const freshBill = freshBills.find((bill) => bill.id === billId);
      const freshItems = freshBill?.elements.flatMap((el) => el.items) ?? [];
      let freshTotal = 0;
      let selectionValid = !!freshBill;
      for (const element of elements) {
        const freshItem = freshItems.find(
          (item) => item.id === element.billElementId,
        );
        if (!freshItem || parseFloat(freshItem.quantity) < element.quantity) {
          selectionValid = false;
          break;
        }
        const additionsTotal = freshItem.additions.reduce(
          (sum, addition) => sum + parseFloat(addition.total_sum),
          0,
        );
        const lineTotal = parseFloat(freshItem.total_sum) + additionsTotal;
        freshTotal +=
          (lineTotal / parseFloat(freshItem.quantity)) * element.quantity;
      }
      if (!selectionValid || Math.abs(freshTotal - totalAmount) > 0.005) {
        // Назад към избора на продукти — той вече показва свежите данни
        message.warning(t("splitBill.billChanged"));
        onClose();
        return;
      }

      const result = await splitAndPayMutation.mutateAsync({
        billId,
        data: {
          elements,
          paymentMethodId: selectedPaymentMethodId,
          mixedCashAmount:
            selectedPaymentMethodId === 3 ? mixedCashAmount : undefined,
          mixedCardAmount:
            selectedPaymentMethodId === 3 ? mixedCardAmount : undefined,
          total: cashOnlyMode || selectedPaymentMethodId === 4 ? 0 : undefined,
        },
      });
      onSuccess();
      const fiscal = result?.fiscal_result;
      if (!fiscal || fiscal.status === "success") {
        message.success(t("splitBill.splitAndPaySuccess"));
      } else if (fiscal.status === "timeout") {
        notifyPrinterTimeout(fiscal.error || t("closeTable.printerTimeout"));
      } else {
        notifyPrinterError(fiscal.error || t("closeTable.printerError"));
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, t("splitBill.splitAndPayError")));
      console.error("Split & pay error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedMethod = methods.find((m) => m.id === selectedPaymentMethodId);
  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPad = isNarrow ? "14px 16px" : "18px 24px";

  return (
    <Modal
      open={open}
      onCancel={() => !submitting && onClose()}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{
          background: R.surface,
          fontFamily: R.font,
          color: R.text,
        }}
        onClick={() => {
          if (cashOnlyMode) setCashClickCount(0);
        }}
      >
        <ModalHeader
          icon={<EuroCircleOutlined />}
          title={t("splitBill.splitAndPayTitle")}
          subtitle={t("splitBill.splitAndPaySubtitle", {
            name: sourceTableName,
          })}
          onClose={() => !submitting && onClose()}
          isNarrow={isNarrow}
        />

        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "48px 24px",
            }}
          >
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 36, color: R.brand }}
                  spin
                />
              }
            />
          </div>
        ) : (
          <>
            <div
              style={{
                padding: bodyPad,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isNarrow ? "column" : "row",
                  gap: 16,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: R.textMuted,
                      textTransform: "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    {t("closeTable.paymentMethod")}
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: R.ink,
                      letterSpacing: -0.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {selectedMethod
                      ? i18n.language === "en"
                        ? selectedMethod.name_en
                        : selectedMethod.name_bg
                      : t("closeTable.notSelected")}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontFamily: R.mono,
                      fontSize: isNarrow ? 32 : 40,
                      fontWeight: 700,
                      color: R.brand,
                      letterSpacing: -1.2,
                      lineHeight: 1,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {totalAmount.toFixed(2)}
                  </div>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: R.textMuted,
                      textTransform: "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    {t("closeTable.selectMethod")}
                  </div>
                  {methods.length === 0 ? (
                    <div>
                      <TablesEmptyState
                        icon={<WalletOutlined />}
                        title={t("closeTable.noMethodsTitle")}
                        description={t("closeTable.noMethodsDescription")}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {/* „По сметка" (4) е СКРИТ без клиент с правото */}
                      {methods
                        .filter((m) => m.id !== 4 || canOnAccount)
                        .map((method) => {
                        const isSelected =
                          selectedPaymentMethodId === method.id;
                        const isDisabled =
                          (method.id === 1 && !canPaymentCash) ||
                          (method.id === 2 && !canPaymentCard) ||
                          (method.id === 3 &&
                            (!canPaymentCash || !canPaymentCard));
                        const isHidden = cashOnlyMode && method.id !== 1;
                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={(e) => {
                              if (isDisabled || isHidden) return;
                              if (method.id === 1 && canCloseBill1) {
                                e.stopPropagation();
                                setCashClickCount((c) => c + 1);
                              }
                              setSelectedPaymentMethodId(method.id);
                            }}
                            disabled={isDisabled}
                            style={{
                              visibility: isHidden ? "hidden" : "visible",
                              padding: "10px 12px",
                              borderRadius: 10,
                              border: `1px solid ${
                                isSelected ? "rgba(217,119,6,0.32)" : R.border
                              }`,
                              background: isSelected
                                ? R.brandTint
                                : R.surface,
                              // Неактивен ред (напр. „По сметка" без клиент) —
                              // БЕЗ топъл фон, сив текст + намалена опасити, за
                              // да чете като недостъпен, а не като избран.
                              opacity: isDisabled ? 0.55 : 1,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              fontFamily: "inherit",
                              textAlign: "left",
                              transition:
                                "background 120ms, border-color 120ms",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 13.5,
                                fontWeight: 600,
                                color: isDisabled
                                  ? R.textMuted
                                  : isSelected
                                    ? R.brandDeep
                                    : R.ink,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {i18n.language === "en"
                                ? method.name_en
                                : method.name_bg}
                            </span>
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 10,
                                background: isSelected ? R.brand : R.surface,
                                border: `1px solid ${
                                  isSelected ? R.brand : R.borderMed
                                }`,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#FFFFFF",
                                flexShrink: 0,
                              }}
                            >
                              {isSelected && (
                                <CheckOutlined style={{ fontSize: 11 }} />
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {selectedPaymentMethodId === 3 && (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: R.bg,
                    border: `1px solid ${R.border}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      color: R.textMuted,
                      textTransform: "uppercase",
                    }}
                  >
                    {t("closeTable.paymentDistribution")}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: R.textMuted,
                          marginBottom: 4,
                        }}
                      >
                        {t("closeTable.cash")}
                      </div>
                      <InputNumber
                        value={mixedCashAmount}
                        onChange={(v) => {
                          const cash = v || 0;
                          setMixedCashAmount(cash);
                          setMixedCardAmount(Math.max(0, totalAmount - cash));
                        }}
                        onFocus={(e) => e.target.select()}
                        parser={(v) =>
                          parseFloat(v?.replace(/,/g, ".") || "0") || 0
                        }
                        min={0}
                        max={totalAmount}
                        precision={2}
                        step={0.01}
                        disabled={submitting}
                        style={{
                          width: "100%",
                          height: 40,
                          borderRadius: 10,
                          borderColor: R.border,
                        }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: R.textMuted,
                          marginBottom: 4,
                        }}
                      >
                        {t("closeTable.card")}
                      </div>
                      <InputNumber
                        value={mixedCardAmount}
                        onChange={(v) => {
                          const card = v || 0;
                          setMixedCardAmount(card);
                          setMixedCashAmount(Math.max(0, totalAmount - card));
                        }}
                        onFocus={(e) => e.target.select()}
                        parser={(v) =>
                          parseFloat(v?.replace(/,/g, ".") || "0") || 0
                        }
                        min={0}
                        max={totalAmount}
                        precision={2}
                        step={0.01}
                        disabled={submitting}
                        style={{
                          width: "100%",
                          height: 40,
                          borderRadius: 10,
                          borderColor: R.border,
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: R.textMuted,
                      fontFamily: R.mono,
                    }}
                  >
                    {t("closeTable.mixedTotal", {
                      entered: (mixedCashAmount + mixedCardAmount).toFixed(2),
                      total: totalAmount.toFixed(2),
                    })}
                  </div>
                </div>
              )}
            </div>

            <ModalFooter isNarrow={isNarrow}>
              <GhostButton
                label={t("common.back")}
                onClick={onClose}
                disabled={submitting}
                shortcut={isDesktop ? "Esc" : undefined}
              />
              <PrimaryButton
                label={t("splitBill.splitAndPayButton")}
                onClick={() => handleSubmitRef.current()}
                disabled={!selectedPaymentMethodId || submitting}
                loading={submitting}
                shortcut={isDesktop ? "⏎" : undefined}
              />
            </ModalFooter>
          </>
        )}
      </div>
    </Modal>
  );
};
